import { forwardRef, memo, useEffect, useMemo, useRef, useState } from 'react';
import { Group, Image, Rect, Text } from 'react-konva';
import Konva from 'konva';
import type { Node } from '../../../core/models/scene';
import type { OrgReportCounts } from '../services/orgChartReportCounts';
import type { NodeScenarioStatus } from '../models/Scenario';
import { buildAccentGradientStops, resolveOrgAccentColor } from './orgChartShapeColors';
import {
  shouldShowScenarioStyling,
  getKonvaDash,
  getScenarioStrokeColor,
  getScenarioStrokeWidth,
  getScenarioFillColor,
  getScenarioOpacity,
  SCENARIO_STATUS_STYLES,
} from './scenarioStatusStyles';
import { User, Users } from 'lucide-react';
import { useLucideIconImage } from './orgChartFooterIcons';
import { useHighlightStore } from '../../../stores/highlightStore';
import { resolveOrgTextStyle } from './orgChartTextStyles';
import { useAnimatedLayoutPosition } from '../hooks/useAnimatedLayoutPosition';
import { useOrgNodeEntrance } from '../hooks/useOrgNodeEntrance';
import { useOrgChartViewStore } from '../../../stores/orgchartViewStore';
import { useThemeAwareColor, useThemeMode } from '../../../react-app/theme/ThemeProvider';
import { useDiagramDocument } from '../../../contexts/DiagramDocumentContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { useDiagramSettings } from '../../../hooks/useDiagramSettings';
import { getOrgTextCardTokens } from '../models/orgTextCardTokens';
import {
  buildCardTintGradientStops,
  buildDarkCardTintGradientStops,
  isNearWhiteColor,
  resolveOrgDash,
  resolveOrgBorderColor,
  resolveOrgStrokeWidth,
} from './orgChartShapeColors';
import type { ComputedNodeStyle } from '../hooks/useViewRules';
import {
  buildVisibleCardFieldMap,
  DEFAULT_POSITION_CARD_VISIBLE_FIELD_IDS,
  getCustomCardFieldOptions,
  getPositionOntologyCardFieldOptions,
  formatOrgCardFieldDisplayValue,
  hasDisplayValue,
  mergeCardFieldOptions,
  resolveNodeCustomFields,
  resolvePositionCardFieldValue,
  translateOrgCardFieldOptionLabel,
} from './orgCardFields';
import { asTouchKonvaHandler } from '../../../lib/konvaEventAdapters';
import { resolveOrgCardDimension, useOrgCardLiveResize } from './orgCardLiveResize';

const getThemeColor = (name: string, fallback: string): string => {
  if (typeof window === 'undefined') return fallback;
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

const createRoundedTopClip = (width: number, height: number, cornerRadius: number) => {
  return (ctx: any) => {
    ctx.beginPath();
    ctx.moveTo(cornerRadius, 0);
    ctx.lineTo(width - cornerRadius, 0);
    ctx.quadraticCurveTo(width, 0, width, cornerRadius);
    ctx.lineTo(width, height);
    ctx.lineTo(0, height);
    ctx.lineTo(0, cornerRadius);
    ctx.quadraticCurveTo(0, 0, cornerRadius, 0);
    ctx.closePath();
  };
};

interface PositionNodeProps {
  node: Node;
  selected: boolean;
  reportCounts?: OrgReportCounts;
  collapseTargetId?: string;
  onToggleCollapse?: (reportingNodeId: string) => void;
  enableCollapseAnimation?: boolean;
  isExiting?: boolean;
  onDragStart?: (pos: { x: number; y: number }) => void;
  onDragMove?: (pos: { x: number; y: number }) => void;
  onDragEnd?: (pos: { x: number; y: number }) => void;
  onClick?: (e: Konva.KonvaEventObject<MouseEvent>) => void;
  onTransformStart?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransformEnd?: (e: Konva.KonvaEventObject<Event>) => void;
  onTransform?: (e: Konva.KonvaEventObject<Event>) => void;
  onStartTextEdit?: (field: 'name' | 'title', textNode: Konva.Text) => void;
  editingField?: 'name' | 'title' | null;
  editingPrimaryLabel?: boolean;
  canWrite?: boolean;
  scenarioStatus?: NodeScenarioStatus;
  viewRuleState?: {
    opacity: number;
    visible: boolean;
    style: ComputedNodeStyle;
  };
}

const PositionNodeInner = forwardRef<Konva.Group, PositionNodeProps>(({
  node,
  selected,
  reportCounts,
  collapseTargetId,
  onToggleCollapse,
  enableCollapseAnimation,
  isExiting,
  onDragStart,
  onDragMove,
  onDragEnd,
  onClick,
  onTransformStart,
  onTransformEnd,
  onTransform,
  onStartTextEdit,
  editingPrimaryLabel,
  canWrite = true,
  scenarioStatus,
  viewRuleState,
}, ref) => {
  const { t } = useTranslation('diagram');
  const { doc } = useDiagramDocument();
  const { read: readSettings } = useDiagramSettings(doc);
  const settings = readSettings();
  const customFieldDefinitions = settings.orgchart?.customFieldDefinitions ?? [];
  const groupRef = useRef<Konva.Group | null>(null);
  const nameTextRef = useRef<Konva.Text | null>(null);
  const titleTextRef = useRef<Konva.Text | null>(null);

  const persistedWidth = resolveOrgCardDimension(node.w ?? node.width, 160);
  const persistedHeight = resolveOrgCardDimension(node.h ?? node.height, 72);
  const { liveSize, handleTransform, handleTransformEnd } = useOrgCardLiveResize({
    groupRef,
    width: persistedWidth,
    height: persistedHeight,
    onTransform,
    onTransformEnd,
  });
  const width = liveSize?.w ?? persistedWidth;
  const height = liveSize?.h ?? persistedHeight;
  const tokens = getOrgTextCardTokens(true);

  const attrs = (node.attrs as Record<string, unknown>) ?? {};
  const props = (node.props as Record<string, unknown>) ?? {};

  const fillColor = (node.fillColor as string) || (attrs.fillColor as string) || '#FFFFFF';
  const defaultNameColor = '#04263D';
  const defaultTitleColor = '#4B5356';
  const textColor = (attrs.textColor as string) || (props.textColor as string) || defaultNameColor;
  const accentColor = resolveOrgAccentColor(attrs, props, node.strokeColor as string);
  const subtitleColor = (attrs.subtitleColor as string) || (props.subtitleColor as string) || defaultTitleColor;
  const getThemeColorForMode = useThemeAwareColor();
  const { mode } = useThemeMode();

  const name = (attrs.name as string) || (props.name as string) || (node.name as string) || '';
  const defaultPositionTitle = t('orgchart.defaultPositionTitle', { defaultValue: 'New Position' }) as string;
  const title = (attrs.title as string) || (props.title as string) || (node.text as string) || defaultPositionTitle;
  const isVacant = Boolean(attrs.unstaffed || props.unstaffed);

  const cornerRadius = tokens.cornerRadius;
  const accentHeight = 6;
  const paddingX = tokens.paddingX;
  const paddingTop = 16;

  const vacantDisplayLabel = t('orgchart.positionVacantLabel', { defaultValue: 'Vacant' }) as string;
  const displayName = name || (isVacant ? vacantDisplayLabel : title);
  const displayTitle = (name || isVacant) ? title : '';
  const nameY = paddingTop;
  const titleY = nameY + tokens.nameLineHeight * 2;
  const nameTextStyle = resolveOrgTextStyle(attrs, { fontStyle: isVacant ? 'italic' : 'bold' }, (node as { fontFamily?: string }).fontFamily);
  const titleTextStyle = resolveOrgTextStyle(attrs, { fontStyle: 'normal' }, (node as { fontFamily?: string }).fontFamily);
  const fontFamily = nameTextStyle.fontFamily ?? 'Inter, system-ui, -apple-system, sans-serif';

  // Check highlight state from store
  const highlightedIds = useHighlightStore((s) => s.highlightedIds);
  const reportingChainIds = useHighlightStore((s) => s.reportingChainIds);
  const highlightColor = useHighlightStore((s) => s.highlightColor);
  
  const isPrimaryHighlight = highlightedIds.has(node.id);
  const isChainHighlight = reportingChainIds.has(node.id);
  const filterOpacity = viewRuleState?.opacity ?? 1;

  const showScenarioStyle = shouldShowScenarioStyling(scenarioStatus);
  const scenarioDash = getKonvaDash(scenarioStatus);
  const scenarioOpacity = getScenarioOpacity(scenarioStatus);
  const combinedOpacity = scenarioOpacity * filterOpacity;
  const scenarioFill = showScenarioStyle ? getScenarioFillColor(scenarioStatus, fillColor) : fillColor;
  const displayRuleStyle = viewRuleState?.style ?? { filterOpacity: 1, filterVisible: true, badges: [], labels: [] };
  const displayBgColor = displayRuleStyle.backgroundColor;
  const displayBorderColor = displayRuleStyle.borderColor;
  const displayTextColor = displayRuleStyle.textColor;
  const resolvedTextColor = getThemeColorForMode(
    displayTextColor || textColor || (mode === 'dark' ? '#EEF4FF' : defaultNameColor),
    { type: 'foreground' },
  );
  const resolvedSubtitleColor = getThemeColorForMode(
    displayTextColor || subtitleColor || (mode === 'dark' ? '#D6E2F3' : defaultTitleColor),
    { type: 'foreground' },
  );
  const baseBorderColor = resolveOrgBorderColor(attrs, props, node.strokeColor as string);
  const baseStrokeWidth = resolveOrgStrokeWidth(attrs, props, node.strokeWidth as number | undefined);
  const nodeDash = (node as unknown as { dash?: number[]; dashEnabled?: boolean }).dash;
  const nodeDashEnabled = (node as unknown as { dash?: number[]; dashEnabled?: boolean }).dashEnabled;
  const baseDash = resolveOrgDash(attrs, props, nodeDash, nodeDashEnabled) ?? [5, 4];
  const shouldUseTintedFill = !showScenarioStyle && isNearWhiteColor(fillColor);
  const subtleFillStops = buildCardTintGradientStops(accentColor);
  const subtleDarkFillStops = buildDarkCardTintGradientStops(accentColor);

  let strokeColor = 'transparent';
  let strokeWidth = 0;

  if (showScenarioStyle) {
    strokeColor = getScenarioStrokeColor(scenarioStatus, selected);
    strokeWidth = getScenarioStrokeWidth(scenarioStatus, selected);
  } else if (selected) {
    strokeColor = '#3B82F6';
    strokeWidth = 2;
  } else if (isPrimaryHighlight) {
    strokeColor = highlightColor;
    strokeWidth = 4;
  } else if (isChainHighlight) {
    strokeColor = highlightColor;
    strokeWidth = 2;
  } else if (displayBorderColor) {
    strokeColor = displayBorderColor;
    strokeWidth = baseStrokeWidth ?? 2;
  } else if (baseBorderColor) {
    strokeColor = baseBorderColor;
    strokeWidth = baseStrokeWidth ?? 1.2;
  } else {
    strokeColor = accentColor;
    strokeWidth = baseStrokeWidth ?? 1.2;
  }
  const shouldUseBaseDash =
    !showScenarioStyle && !selected && !isPrimaryHighlight && !isChainHighlight;
  const effectiveDash = showScenarioStyle
    ? scenarioDash
    : shouldUseBaseDash
      ? baseDash
      : undefined;
  const themedFillColor = getThemeColorForMode(displayBgColor || fillColor, { type: 'background' });
  const themedStrokeColor = mode === 'dark'
    ? getThemeColorForMode(strokeColor, { type: 'accent' })
    : getThemeColorForMode(strokeColor, { type: 'foreground' });

  const accentGradientStops = buildAccentGradientStops(accentColor, 'bar');

  const directReports = reportCounts?.directReports ?? 0;
  const totalReports = reportCounts?.totalReports ?? 0;
  const showFooter = directReports > 0 || totalReports > 0;
  const footerHeight = showFooter ? 26 : 0;
  const footerPaddingX = 16;
  const footerLabelFontSize = 12;
  const footerValueFontSize = 12;
  const [hoveredFooter, setHoveredFooter] = useState<'direct' | 'total' | null>(null);
  const tooltipBg = useMemo(() => getThemeColor('--primary', '#08436B'), []);
  const tooltipFg = useMemo(() => getThemeColor('--primary-foreground', '#FFFFFF'), []);
  const footerIconSize = 12;
  const directIcon = useLucideIconImage(User, footerIconSize, '#6B7280');
  const totalIcon = useLucideIconImage(Users, footerIconSize, '#6B7280');
  const handleFooterClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!onToggleCollapse || !collapseTargetId) return;
    e.cancelBubble = true;
    onToggleCollapse(collapseTargetId);
  };

  const customFields = useMemo(
    () => resolveNodeCustomFields({
      nodeType: node.type,
      attrs,
      props,
      definitions: customFieldDefinitions,
    }),
    [node.type, attrs, props, customFieldDefinitions],
  );
  const allFieldOptions = useMemo(
    () => mergeCardFieldOptions(getPositionOntologyCardFieldOptions(), getCustomCardFieldOptions(customFields)),
    [customFields],
  );
  const visibleFields = useMemo(
    () =>
      buildVisibleCardFieldMap(
        attrs.visibleFields as Record<string, boolean> | undefined,
        allFieldOptions,
        DEFAULT_POSITION_CARD_VISIBLE_FIELD_IDS,
      ),
    [attrs.visibleFields, allFieldOptions],
  );
  const positionDetailLines = useMemo(() => {
    const lines: string[] = [];
    for (const option of allFieldOptions) {
      if (!visibleFields[option.id]) continue;
      const value = resolvePositionCardFieldValue({
        fieldId: option.id,
        attrs,
        props,
        nodeName: name || title,
        customFields,
      });
      if (!hasDisplayValue(value)) continue;
      const label =
        option.id === 'plannedFillBy'
          ? (t('orgchart.cardFieldShortLabels.plannedFillBy', { defaultValue: 'Fill by' }) as string)
          : translateOrgCardFieldOptionLabel(t, option);
      const displayVal = formatOrgCardFieldDisplayValue(t, value);
      lines.push(`${label}: ${displayVal}`);
    }
    return lines;
  }, [allFieldOptions, visibleFields, attrs, props, name, title, customFields, t]);
  const primaryDetailLine = positionDetailLines[0] ?? '';

  const fireDrag = (handler?: (pos: { x: number; y: number }) => void) => (e: Konva.KonvaEventObject<DragEvent>) => {
    if (!handler) return;
    const pos = e.target.position();
    handler({ x: pos.x, y: pos.y });
  };

  const isFooterHit = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!showFooter) return false;
    const stage = e.target.getStage();
    const pointer = stage?.getPointerPosition();
    if (!pointer || !groupRef.current) return false;
    const transform = groupRef.current.getAbsoluteTransform().copy().invert();
    const local = transform.point(pointer);
    return local.y >= height - footerHeight;
  };

  const handleDoubleClick = (e: Konva.KonvaEventObject<MouseEvent>) => {
    if (!canWrite) return;
    if (isFooterHit(e)) return;
    e.cancelBubble = true;
    e.evt?.stopPropagation?.();
    e.evt?.preventDefault?.();
    if (!onStartTextEdit) return;
    const textNode = displayTitle ? titleTextRef.current : nameTextRef.current;
    if (textNode) onStartTextEdit('title', textNode);
  };

  // Animated position transitions
  const suppressLayoutAnim = useAnimatedLayoutPosition(
    groupRef, node.x, node.y,
    { enabled: !!enableCollapseAnimation && !isExiting },
  );

  // Collapse highlight: flash the parent node when a branch is collapsed
  const pendingCollapseHL = useOrgChartViewStore(s => s.pendingCollapseHighlight);
  const collapseHL = !!pendingCollapseHL && pendingCollapseHL === collapseTargetId;
  // Outline-first entrance animation + collapse highlight
  const entrance = useOrgNodeEntrance(
    !!enableCollapseAnimation,
    !!isExiting,
    width,
    height,
    cornerRadius,
    collapseHL,
  );

  // Exit animation (unchanged)
  const didExitRef = useRef(false);
  useEffect(() => {
    if (!enableCollapseAnimation || !isExiting || didExitRef.current) return;
    const nodeEl = groupRef.current;
    if (!nodeEl) return;
    didExitRef.current = true;
    nodeEl.opacity(1);
    const tween = new Konva.Tween({
      node: nodeEl,
      duration: 0.15,
      opacity: 0,
    });
    tween.play();
    return () => {
      tween.destroy();
    };
  }, [enableCollapseAnimation, isExiting]);

  // Hide mode: don't render non-matching nodes
  if (viewRuleState && !viewRuleState.visible) {
    return null;
  }

  return (
    <Group
      ref={(nodeEl) => {
        groupRef.current = nodeEl;
        if (typeof ref === 'function') ref(nodeEl);
        else if (ref && typeof ref === 'object') (ref as React.MutableRefObject<Konva.Group | null>).current = nodeEl;
      }}
      id={node.id}
      name="shape"
      x={node.x}
      y={node.y}
      width={width}
      height={height}
      draggable={canWrite && !isExiting && !editingPrimaryLabel}
      listening={!isExiting}
      onDragStart={(e) => { suppressLayoutAnim.current = true; fireDrag(onDragStart)(e); }}
      onDragMove={fireDrag(onDragMove)}
      onDragEnd={(e) => { fireDrag(onDragEnd)(e); suppressLayoutAnim.current = false; }}
      onClick={onClick}
      onTap={asTouchKonvaHandler(onClick)}
      onTransformStart={onTransformStart}
      onTransformEnd={handleTransformEnd}
      onTransform={handleTransform}
      onDblClick={handleDoubleClick}
      opacity={combinedOpacity}
    >
      {entrance.outlineProps && <Rect {...entrance.outlineProps} />}
      <Group ref={entrance.contentGroupRef}>
      {/* Card background */}
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill={showScenarioStyle
          ? getThemeColorForMode(scenarioFill, { type: 'background' })
          : (shouldUseTintedFill ? undefined : themedFillColor)}
        fillLinearGradientStartPoint={showScenarioStyle || !shouldUseTintedFill ? undefined : { x: 0, y: 0 }}
        fillLinearGradientEndPoint={showScenarioStyle || !shouldUseTintedFill ? undefined : { x: 0, y: height }}
        fillLinearGradientColorStops={showScenarioStyle || !shouldUseTintedFill
          ? undefined
          : (mode === 'dark' ? subtleDarkFillStops : subtleFillStops)}
        stroke={themedStrokeColor}
        strokeWidth={strokeWidth}
        cornerRadius={cornerRadius}
        shadowEnabled={false}
        dash={effectiveDash}
        dashEnabled={Boolean(effectiveDash && effectiveDash.length > 0)}
        opacity={1}
      />

      {/* Top gradient accent bar with rounded top corners using clipping mask */}
      <Group
        clipFunc={createRoundedTopClip(width, accentHeight, cornerRadius)}
        listening={false}
      >
        <Rect
          x={0}
          y={0}
          width={width}
          height={accentHeight}
          fillLinearGradientStartPoint={{ x: 0, y: 0 }}
          fillLinearGradientEndPoint={{ x: width, y: 0 }}
          fillLinearGradientColorStops={accentGradientStops}
          listening={false}
        />
      </Group>

      {/* Name text */}
      {displayName && (
        <Text
          ref={nameTextRef}
          x={paddingX}
          y={nameY}
          width={width - paddingX * 2}
          height={tokens.nameLineHeight * 2}
          text={displayName}
          fontSize={tokens.nameFontSize}
          fontFamily={fontFamily}
          lineHeight={tokens.nameLineHeight / tokens.nameFontSize}
          wrap="word"
          fontStyle={nameTextStyle.fontStyle}
          textDecoration={nameTextStyle.textDecoration}
          fill={isVacant ? '#6B7280' : resolvedTextColor}
          visible={!editingPrimaryLabel}
          listening={false}
          ellipsis
        />
      )}

      {/* Title text (accent color) */}
      {(displayTitle || primaryDetailLine) && (
        <Text
          ref={titleTextRef}
          x={paddingX}
          y={titleY}
          width={width - paddingX * 2}
          height={tokens.titleLineHeight * 2}
          text={displayTitle || primaryDetailLine}
          fontSize={tokens.titleFontSize}
          fontFamily={fontFamily}
          lineHeight={tokens.titleLineHeight / tokens.titleFontSize}
          wrap="word"
          fontStyle={titleTextStyle.fontStyle}
          textDecoration={titleTextStyle.textDecoration}
          fill={resolvedSubtitleColor}
          visible={!editingPrimaryLabel}
          listening={false}
          ellipsis
        />
      )}

      {showFooter && (
        <>
          <Rect
            x={footerPaddingX}
            y={height - footerHeight}
            width={width - footerPaddingX * 2}
            height={1}
            fill="#E5E7EB"
            listening={false}
          />
          <Group y={height - footerHeight + 6}>
            {(() => {
              const leftX = footerPaddingX;
              const gap = 16;
              const rowHeight = footerHeight - 10;
              const rowCenterY = rowHeight / 2;
              const tooltipPaddingX = 8;
              const tooltipPaddingY = 8;
              const tooltipArrowSize = 8;
              const directLabel = t('orgchart.nodeFooter.directReports', { defaultValue: 'Direct reports' });
              const totalLabel = t('orgchart.nodeFooter.totalReports', { defaultValue: 'Total reports' });
              const estimateTooltipWidth = (label: string) => (
                Math.max(64, label.length * (footerLabelFontSize * 0.55) + tooltipPaddingX * 2)
              );
              const directTooltipWidth = estimateTooltipWidth(directLabel);
              const totalTooltipWidth = estimateTooltipWidth(totalLabel);
              const tooltipHeight = footerLabelFontSize + tooltipPaddingY * 2;
              const directValueX = leftX + footerIconSize + 6;
              const directTextWidth = String(directReports).length * (footerValueFontSize * 0.6);
              const directGroupWidth = footerIconSize + 6 + directTextWidth;
              const totalIconX = leftX + directGroupWidth + gap;
              const totalValueX = totalIconX + footerIconSize + 6;
              const totalTextWidth = String(totalReports).length * (footerValueFontSize * 0.6);
              const totalGroupWidth = footerIconSize + 6 + totalTextWidth;
              const directHitWidth = directGroupWidth + 8;
              const totalHitWidth = totalGroupWidth + 8;
              return (
                <>
                  <Rect
                    x={leftX}
                    y={0}
                    width={directHitWidth}
                    height={footerHeight - 6}
                    fill="transparent"
                    onClick={handleFooterClick}
                    onTap={asTouchKonvaHandler(handleFooterClick)}
                    onMouseEnter={() => setHoveredFooter('direct')}
                    onMouseLeave={() => setHoveredFooter(null)}
                  />
                  {directIcon && (
                    <Image
                      image={directIcon}
                      x={leftX}
                      y={rowCenterY - footerIconSize / 2}
                      width={footerIconSize}
                      height={footerIconSize}
                      listening={false}
                    />
                  )}
                  <Text
                    x={directValueX}
                    y={rowCenterY - footerValueFontSize / 2}
                    width={directGroupWidth}
                    text={String(directReports)}
                    fontSize={footerValueFontSize}
                    fontFamily={fontFamily}
                    fontStyle="bold"
                    fill="#111827"
                    listening={false}
                  />
                  {hoveredFooter === 'direct' && (
                    <Group
                      x={leftX + directHitWidth / 2 - directTooltipWidth / 2}
                      y={-tooltipHeight - tooltipArrowSize}
                      listening={false}
                    >
                      <Rect
                        width={directTooltipWidth}
                        height={tooltipHeight}
                        fill={tooltipBg}
                        cornerRadius={6}
                      />
                      <Rect
                        x={directTooltipWidth / 2}
                        y={tooltipHeight - tooltipArrowSize / 2}
                        width={tooltipArrowSize}
                        height={tooltipArrowSize}
                        fill={tooltipBg}
                        rotation={45}
                        offsetX={tooltipArrowSize / 2}
                        offsetY={tooltipArrowSize / 2}
                      />
                      <Text
                        x={tooltipPaddingX}
                        y={0}
                        width={directTooltipWidth - tooltipPaddingX * 2}
                        height={tooltipHeight}
                        text={directLabel}
                        fontSize={footerLabelFontSize}
                        fontFamily={fontFamily}
                        fill={tooltipFg}
                        align="center"
                        verticalAlign="middle"
                      />
                    </Group>
                  )}

                  <Rect
                    x={totalIconX}
                    y={0}
                    width={totalHitWidth}
                    height={footerHeight - 6}
                    fill="transparent"
                    onClick={handleFooterClick}
                    onTap={asTouchKonvaHandler(handleFooterClick)}
                    onMouseEnter={() => setHoveredFooter('total')}
                    onMouseLeave={() => setHoveredFooter(null)}
                  />
                  {totalIcon && (
                    <Image
                      image={totalIcon}
                      x={totalIconX}
                      y={rowCenterY - footerIconSize / 2}
                      width={footerIconSize}
                      height={footerIconSize}
                      listening={false}
                    />
                  )}
                  <Text
                    x={totalValueX}
                    y={rowCenterY - footerValueFontSize / 2}
                    width={totalGroupWidth}
                    text={String(totalReports)}
                    fontSize={footerValueFontSize}
                    fontFamily={fontFamily}
                    fontStyle="bold"
                    fill="#111827"
                    listening={false}
                  />
                  {hoveredFooter === 'total' && (
                    <Group
                      x={totalIconX + totalHitWidth / 2 - totalTooltipWidth / 2}
                      y={-tooltipHeight - tooltipArrowSize}
                      listening={false}
                    >
                      <Rect
                        width={totalTooltipWidth}
                        height={tooltipHeight}
                        fill={tooltipBg}
                        cornerRadius={6}
                      />
                      <Rect
                        x={totalTooltipWidth / 2}
                        y={tooltipHeight - tooltipArrowSize / 2}
                        width={tooltipArrowSize}
                        height={tooltipArrowSize}
                        fill={tooltipBg}
                        rotation={45}
                        offsetX={tooltipArrowSize / 2}
                        offsetY={tooltipArrowSize / 2}
                      />
                      <Text
                        x={tooltipPaddingX}
                        y={0}
                        width={totalTooltipWidth - tooltipPaddingX * 2}
                        height={tooltipHeight}
                        text={totalLabel}
                        fontSize={footerLabelFontSize}
                        fontFamily={fontFamily}
                        fill={tooltipFg}
                        align="center"
                        verticalAlign="middle"
                      />
                    </Group>
                  )}
                </>
              );
            })()}
          </Group>
        </>
      )}

      {/* Scenario status badge */}
      {showScenarioStyle && scenarioStatus && scenarioStatus !== 'unchanged' && (
        <Group x={width - 8} y={-8} listening={false}>
          <Rect
            x={-20}
            y={0}
            width={28}
            height={16}
            fill={SCENARIO_STATUS_STYLES[scenarioStatus].borderColor}
            cornerRadius={4}
          />
          <Text
            x={-20}
            y={0}
            width={28}
            height={16}
            text={scenarioStatus === 'added' ? '+' : scenarioStatus === 'removed' ? '−' : scenarioStatus === 'moved' ? '↔' : '∆'}
            fontSize={11}
            fontStyle="bold"
            fill="#FFFFFF"
            align="center"
            verticalAlign="middle"
          />
        </Group>
      )}
      </Group>
      {entrance.highlightProps && <Rect ref={entrance.highlightRef} {...entrance.highlightProps} />}
    </Group>
  );
});

PositionNodeInner.displayName = 'PositionNode';

export const PositionNode = memo(PositionNodeInner);
export default PositionNode;
